import axios from "axios";
import { deleteCookie, getCookie } from "@/utils/cookieUtils";

const api = axios.create({
	baseURL: import.meta.env.VITE_FLEETER_BACKEND_API_URL,
	headers: {
		"Content-Type": "application/json",
		"x-api-key": import.meta.env.VITE_FLEETER_BACKEND_API_KEY,
	},
});

// Adiciona o token do login automaticamente em todas as requests
api.interceptors.request.use((config) => {
	if (typeof window !== "undefined") {
		const token = getCookie("token");

		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
	}
	return config;
});

// Flag para evitar múltiplos redirecionamentos simultâneos
let isRedirecting = false;

// Interceptor de resposta para tratar erros de autenticação
api.interceptors.response.use(
	(response) => response,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: same flow as staging + export 403 skip
	(error) => {
		if (typeof window !== "undefined") {
			// Se receber 401 (Unauthorized) ou 403 (Forbidden), faz logout e redireciona
			// biome-ignore lint/style/useCollapsedIf: keep staging structure for easier review
			if (error.response?.status === 401 || error.response?.status === 403) {
				const currentPath = window.location.pathname;
				const isLoginPage =
					currentPath === "/login" || currentPath === "/login/";
				const requestUrl = error.config?.url || "";
				const isLoginRequest = requestUrl.includes("/api/auth/login");
				const isAuthMeRequest = requestUrl.includes("/api/auth/me");
				// 403 de domínio na exportação (sem permissão para o relatório), não sessão.
				const isExportForbidden =
					error.response?.status === 403 && requestUrl.includes("/api/export");

				// Não faz redirect se:
				// 1. Já está na página de login (permite exibir o erro)
				// 2. A requisição é para a rota de login (erro de credenciais deve ser tratado pelo componente)
				// 3. Já está redirecionando (evita loops)
				// 4. É uma requisição para /api/auth/me durante o processo de login (pode falhar temporariamente)
				// 5. É 403 de permissão de exportação (domínio, não sessão)
				if (
					!(isLoginPage || isLoginRequest || isRedirecting || isExportForbidden)
				) {
					// Se for uma requisição /api/auth/me e não tiver token, não redireciona
					// (pode ser uma verificação inicial que falhou)
					const hasToken = getCookie("token");

					if (!hasToken && isAuthMeRequest) {
						// Se não tem token e é uma requisição de /me, apenas rejeita o erro
						return Promise.reject(error);
					}

					// Evita múltiplos redirecionamentos
					isRedirecting = true;

					// Limpa o token e dados do usuário
					deleteCookie("token");
					localStorage.removeItem("user");

					// Usa setTimeout para garantir que o React termine de processar antes do redirect
					setTimeout(() => {
						try {
							// Verifica novamente se ainda não está na página de login
							if (
								window.location.pathname !== "/login" &&
								window.location.pathname !== "/login/"
							) {
								window.location.href = "/login/";
							}
						} catch (err) {
							// biome-ignore lint/suspicious/noConsole: same as staging — diagnose redirect failures
							console.error("Erro ao redirecionar para login:", err);
						} finally {
							// Reseta a flag após um tempo para permitir novos redirecionamentos se necessário
							setTimeout(() => {
								isRedirecting = false;
							}, 1000);
						}
					}, 100);
				}
			}
		}
		return Promise.reject(error);
	}
);

export default api;
